import { useCallback, useEffect, useMemo, useState } from 'react'

import classNames from 'classnames'
import { BoardToolbarAction } from 'shared/constants/toolbar'
import ReactDOM from 'react-dom'
import { Button, CloseOutlined, Dropdown, MoreOutlined, Space } from 'shared/ui'
import styled from 'styled-components'

type ItemType = 'board' | 'asset' | 'mixed' | 'none'

interface SelectedItem {
  id: string
  [key: string]: any
}

interface Action {
  id: string
  key?: string
  name: string
  type?: 'divider'
}

interface ToolbarTopProps {
  selectedItems: SelectedItem[]
  mainActions: Action[]
  extraActionsGroups?: Action[][]
  onActionClick?: (itemsType: ItemType, actionId: string, selectedItems: any[]) => void
  onClose?: () => void
  itemsType: ItemType
  multiselectSupportedActions: BoardToolbarAction[]
  actionsIcons: Record<string, React.ReactNode>
  isShown?: boolean
}

const DEFAULT_MULTISELECT_ACTIONS = [
  BoardToolbarAction.Move,
  BoardToolbarAction.Copy,
  BoardToolbarAction.Delete,
  BoardToolbarAction.Download,
  BoardToolbarAction.RemoveFromBoard,
  // BoardToolbarAction.MakeFavorite
]

export function ToolbarTop({
  selectedItems = [],
  mainActions = [],
  onActionClick = () => {},
  onClose = () => {},
  extraActionsGroups = [],
  itemsType = 'none',
  multiselectSupportedActions = DEFAULT_MULTISELECT_ACTIONS,
  actionsIcons = {},
  isShown = true
}: ToolbarTopProps) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  const validActionPredicate = useCallback((action: Action) => {
    const isSingleSelect = selectedItems.length === 1
    const isMultiselectSupported = multiselectSupportedActions.includes(action.key as BoardToolbarAction || action.id as BoardToolbarAction)
    return isSingleSelect || isMultiselectSupported
  }, [selectedItems.length, multiselectSupportedActions])

  const handleActionClick = useCallback((action: Action) => {
    const actionId = action.key || action.id
    onActionClick(itemsType, actionId, selectedItems)
  }, [itemsType, selectedItems, onActionClick])

  const activeActions = useMemo(() => {
    const result: (Action | { type: 'divider' })[] = []

    if (itemsType === 'none') {
      return []
    }

    if(extraActionsGroups && extraActionsGroups.length > 0) {
      extraActionsGroups.forEach((group, index) => {
        const validActions = group
          .filter(validActionPredicate)
          .map(action => ({
            ...action,
            onClick: () => handleActionClick(action)
          }))

        result.push(...validActions)

        if (index < extraActionsGroups.length - 1 && validActions.length > 0) {
          result.push({ type: 'divider' })
        }
      })
    }

    return result
  }, [extraActionsGroups, validActionPredicate, handleActionClick])

  const menuProps = useMemo(() => ({
    items: activeActions,
    className: 'js-page-toolbar-top-dropdown'
  }), [activeActions, handleActionClick])

  const itemLabel = useMemo(() => {
    const count = selectedItems.length
    if (itemsType === 'board') {
      return count === 1 ? 'board' : 'boards'
    } else if (itemsType === 'asset') {
      return count === 1 ? 'asset' : 'assets'
    } else {
      return count === 1 ? 'item' : 'items'
    }
  }, [itemsType, selectedItems.length])

  const content = (
    <SToolbarContainer className={classNames('js-page-toolbar-top', { visible: isShown })}>
      <SToolbarContent>
        <SSelectedInfo>
          <Space>
            <SSelectedCount>
              {selectedItems.length}
            </SSelectedCount>

            {itemLabel}
          </Space>
        </SSelectedInfo>

        <SToolbarActions>
          {mainActions.map(action =>
            validActionPredicate(action) && (
              <SActionButton
                key={action.id}
                variant="text"
                color="default"
                icon={actionsIcons[action.id]}
                onClick={() => handleActionClick(action)}
                style={{ color: 'white', fontSize: '15px' }}
              >
                {action.name}
              </SActionButton>
            )
          )}

          {menuProps.items.length > 0 && (
            <Dropdown menu={menuProps}
              placement="bottomLeft"
            >
              <SActionButton
                variant="text"
                color="default"
                icon={<MoreOutlined style={{ fontSize: 24 }} />}
                style={{ color: 'white', fontSize: 24 }}
              />
            </Dropdown>
          )}
        </SToolbarActions>

        <SCloseButton
          variant="text"
          color="default"
          icon={<CloseOutlined />}
          onClick={onClose}
          style={{ color: 'white', fontSize: 20 }}
        />
      </SToolbarContent>
    </SToolbarContainer>
  )

  return mounted ? ReactDOM.createPortal(content, document.body) : null
}

const SToolbarContainer = styled.div`
  position: fixed;
  top: 8px;
  color: white;
  z-index: 1000;
  //max-width: 1250px;
  width: 100%;
  left: 50%;
  transition: transform 0.3s ease;
  transform: translateX(-50%) translateY(calc(-100% - 8px));

  &.visible {
    transform: translateX(-50%) translateY(0);
  }
`

const SToolbarContent = styled.div`
  display: grid;
  grid-template-columns: 355px 1fr 20px;
  max-width: calc(100% - 360px);
  margin: 0 30px 0 286px;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background-color: #222222;
  border-radius: 8px;
  padding: 12px 24px;
  width: 100%;
`

const SSelectedInfo = styled.div`
  font-size: 15px;
  font-weight: 400;
  display: flex;
  align-items: center;
`

const SSelectedCount = styled.span`
  font-weight: 600;
`

const SToolbarActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  align-items: center;
`

const SActionButton = styled(Button)`
  && {
    &[ant-click-animating-without-extra-node]:after {
      animation: none !important;
    }
  }
`

const SCloseButton = styled(Button)`
  && {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`
